/* eslint-disable @next/next/no-img-element */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatableField } from '@/utils/useTranslatableField';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ClearLocalStorage } from '@/screens/survey/SurveyLocalStorageHelpers';
import { PageContext } from '@/context/PageContext';
import { useIsEmbed } from 'utils/useIsEmbed';
import classNames from 'classnames';
import { TranslationMenu } from 'components/Atoms/TranslationMenu/TranslationMenu';
import { determineThemeName } from 'utils/determineUrls';
import { determineLanguageMenuLanguages } from 'utils/determineLanguageMenuLanguages';
import { buildPublicPathFromSlug } from 'utils/publicUrlRule';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import styles from './Footer.module.scss';

interface Props {
  isEngagement?: boolean;
}
export function Footer(props: Props) {
  const { isEngagement } = props;
  const { t } = useTranslation();
  const { theme, survey, page, questionnaire, urlParams, surveyComplete, slug, linkHostname } = useContext(PageContext);
  const { translateField } = useTranslatableField(urlParams?.locale);
  const { isEmbed } = useIsEmbed();
  const languages = determineLanguageMenuLanguages({ theme, questionnaire, survey, page });
  const onResetSurvey = () => {
    ClearLocalStorage(survey?.structure?.id || '');
    window?.location?.reload();
  };

  const year = new Date().getFullYear();
  // Both segments are required: without them the path collapses to a protocol-relative `//…` URL.
  const organization = urlParams?.organization;
  const locale = urlParams?.locale;
  if (isEmbed) {
    return null;
  }
  return (
    <footer
      className={classNames(styles.footerContainer, {
        [styles.isEngagement]: isEngagement,
      })}
    >
      <div className={styles.footerText}>
        © {year} {determineThemeName(theme, translateField) || ''} <Spacer orientation="horizontal" size="small" />
        <a href="https://zencity.io/privacy-policy/" className="is-secondary-color" target="_blank" rel="noreferrer">
          {t('footer.privacy')}
        </a>
        <Spacer orientation="horizontal" size="small" />
        <a
          href="https://zencity.io/accessibility-statement/"
          className="is-secondary-color"
          target="_blank"
          rel="noreferrer"
        >
          {t('footer.accessibilityStatement')}
        </a>
        <Spacer orientation="horizontal" size="small" />
        <a href="https://zencity.io/cookie-policy/" className="is-secondary-color" target="_blank" rel="noreferrer">
          {t('footer.cookiePolicy')}
        </a>
        {organization && locale && (
          <>
            <Spacer orientation="horizontal" size="small" />
            <a
              href={buildPublicPathFromSlug([organization, locale, 'sitemap'], linkHostname)}
              className="is-secondary-color"
            >
              {t('sitemap')}
            </a>
          </>
        )}
        <div className={styles.footerPoweredBy}>
          {t('footer.poweredBy')} <img src="https://logo.zencity.io/logo.svg" alt={t('icons.zencity')} />
        </div>
      </div>

      <div className={styles.footerButtons}>
        <div className={styles.translation}>
          <TranslationMenu slug={slug} languages={languages} />
        </div>
        {survey?.kiosk_mode && surveyComplete && (
          <button
            id="surveyRestartButton"
            data-testid="restartSurveyButton"
            type="button"
            className={styles.surveyRestart}
            onClick={onResetSurvey}
          >
            <Icon name="update" />
            {t('survey.restart')}
          </button>
        )}
      </div>
    </footer>
  );
}
